import { Project } from '@/types/product-manager';
import { AVAILABLE_APPS } from './appAccess';
import { createProject, getProjects } from './productManagerService';
import { Timestamp } from 'firebase/firestore';

// Default app configurations for apps that don't have custom metadata
const DEFAULT_APP_CONFIGS: Record<string, {
  name: string;
  description: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  category: string;
  appPath: string;
  appColor: string;
  appIcon: string;
  tags: string[];
}> = {
  'good-things': {
    name: 'Good Thing Tracker',
    description: 'Track and celebrate team achievements and recognition',
    priority: 'medium',
    category: 'Team Management',
    appPath: '/good-things',
    appColor: 'bg-pink-500',
    appIcon: 'Heart',
    tags: ['team', 'recognition', 'achievements']
  },
  'sop': {
    name: 'SOP Central',
    description: 'Access and manage Standard Operating Procedures',
    priority: 'high',
    category: 'Documentation',
    appPath: '/sop',
    appColor: 'bg-green-500',
    appIcon: 'Book',
    tags: ['documentation', 'procedures', 'knowledge']
  },
  'plms': {
    name: 'JLS PLMS',
    description: 'Track and manage personal learning journey',
    priority: 'medium',
    category: 'Learning',
    appPath: '/plms',
    appColor: 'bg-emerald-500',
    appIcon: 'School',
    tags: ['learning', 'development', 'personal']
  },
  'pmt2': {
    name: 'PMT3',
    description: 'Monitor and report on Amazon seller data',
    priority: 'high',
    category: 'Analytics',
    appPath: '/pmt3',
    appColor: 'bg-orange-500',
    appIcon: 'Database',
    tags: ['analytics', 'amazon', 'data']
  },
  'dayaway': {
    name: 'DayAway Board',
    description: 'Plan and celebrate team rest and recovery time',
    priority: 'medium',
    category: 'Team Management',
    appPath: '/day-away',
    appColor: 'bg-cyan-500',
    appIcon: 'CalendarIcon',
    tags: ['team', 'time-off', 'calendar']
  },
  'ideogram': {
    name: 'Ideogram PromptStorm',
    description: 'Create and manage AI-generated images',
    priority: 'low',
    category: 'Creative Tools',
    appPath: '/ideogram',
    appColor: 'bg-indigo-500',
    appIcon: 'Image',
    tags: ['ai', 'images', 'creative']
  },
  'inventory': {
    name: 'Listing Management System',
    description: 'Optimize and manage product listings',
    priority: 'high',
    category: 'E-commerce',
    appPath: '/lms',
    appColor: 'bg-blue-500',
    appIcon: 'Store',
    tags: ['ecommerce', 'listings', 'optimization']
  },
  'idea-machine': {
    name: 'The Idea Machine',
    description: 'Generate and track creative ideas systematically',
    priority: 'low',
    category: 'Creative Tools',
    appPath: '/idea-machine',
    appColor: 'bg-purple-500',
    appIcon: 'Lightbulb',
    tags: ['ideas', 'creativity', 'brainstorming']
  },
  'ai-devquest': {
    name: 'AI DevQuest',
    description: 'Coding with AI Ecourse - Learn AI development skills',
    priority: 'medium',
    category: 'Learning',
    appPath: '/ai-devquest',
    appColor: 'bg-gray-700',
    appIcon: 'BookOpen',
    tags: ['ai', 'learning', 'development']
  },
  'glossary': {
    name: 'Glossary',
    description: 'Access and manage company terminology and definitions',
    priority: 'medium',
    category: 'Documentation',
    appPath: '/glossary',
    appColor: 'bg-teal-500',
    appIcon: 'BookText',
    tags: ['documentation', 'terminology', 'knowledge']
  },
  'employee-directory': {
    name: 'Employee Directory',
    description: 'Browse and manage employee information with global visualization',
    priority: 'medium',
    category: 'HR Management',
    appPath: '/employee-directory',
    appColor: 'bg-blue-600',
    appIcon: 'Users',
    tags: ['hr', 'employees', 'directory']
  },
  'product-manager': {
    name: 'Product Manager',
    description: 'Collaborative project and task management for JLS Apps development',
    priority: 'high',
    category: 'Project Management',
    appPath: '/product-manager',
    appColor: 'bg-violet-600',
    appIcon: 'FolderKanban',
    tags: ['project-management', 'tasks', 'collaboration']
  },
  'ppc-farm': {
    name: 'PPC Farm Mail Tracker',
    description: 'A tool to keep track of direct mail campaigns from PPC Farm.',
    priority: 'medium',
    category: 'Productivity',
    appPath: '/ppc-farm',
    appColor: 'bg-yellow-500',
    appIcon: 'ClipboardList',
    tags: ['direct-mail', 'tracking', 'campaigns']
  },
  'farming-results': {
    name: 'Farming Results',
    description: 'Visualize and manage CSTs, bids, and farming history',
    priority: 'medium',
    category: 'Data Visualization and Management',
    appPath: '/farming-results',
    appColor: 'bg-green-500',
    appIcon: 'ChartLine',
    tags: ['farming', 'csts', 'analytics', 'amazon']
  }
};

// Generate default configuration for unknown apps
const generateDefaultAppConfig = (appId: string, appKey: string) => {
  // Convert kebab-case to Title Case
  const name = appId.split('-').map(word =>
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');
  
  // Generate a path from the app ID
  const appPath = `/${appId}`;
  
  // Use a default color and icon
  const defaultColors = [
    'bg-slate-600', 'bg-zinc-600', 'bg-neutral-600', 'bg-stone-600',
    'bg-red-600', 'bg-amber-600', 'bg-yellow-600', 'bg-lime-600',
    'bg-green-600', 'bg-emerald-600', 'bg-teal-600', 'bg-cyan-600',
    'bg-sky-600', 'bg-blue-600', 'bg-indigo-600', 'bg-violet-600',
    'bg-purple-600', 'bg-fuchsia-600', 'bg-pink-600', 'bg-rose-600'
  ];
  
  // Use hash of appId to consistently pick a color
  const colorIndex = appId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % defaultColors.length;
  
  return {
    name,
    description: `${name} application - part of the JLS Apps ecosystem`,
    priority: 'medium' as const,
    category: 'General',
    appPath,
    appColor: defaultColors[colorIndex],
    appIcon: 'Package', // Default icon
    tags: ['app', 'jls-apps']
  };
};

// Dynamically generate JLS Apps projects from AVAILABLE_APPS
export const generateJLSAppsProjects = (): Omit<Project, 'id' | 'createdAt' | 'updatedAt'>[] => {
  const projects: Omit<Project, 'id' | 'createdAt' | 'updatedAt'>[] = [];
  
  // Iterate through all available apps
  Object.entries(AVAILABLE_APPS).forEach(([appKey, appId]) => {
    // Get configuration (custom or default)
    const config = DEFAULT_APP_CONFIGS[appId] || generateDefaultAppConfig(appId, appKey);
    
    projects.push({
      name: config.name,
      description: config.description,
      status: 'active',
      type: 'live_app',
      appId: appId,
      createdBy: 'system',
      metadata: {
        priority: config.priority,
        category: config.category,
        appPath: config.appPath,
        appColor: config.appColor,
        appIcon: config.appIcon,
        tags: config.tags
      }
    });
  });
  
  return projects;
};

// Legacy static array for backward compatibility (now dynamically generated)
export const JLS_APPS_PROJECTS = generateJLSAppsProjects();

/**
 * Automatically sync all apps from AVAILABLE_APPS to live projects
 * This function will create projects for any new apps added to AVAILABLE_APPS
 */
export const initializeLiveProjects = async (): Promise<void> => {

  try {
    // Generate current projects from AVAILABLE_APPS
    const currentAppsProjects = generateJLSAppsProjects();

    // Get all existing projects
    const existingProjects = await getProjects();
    const existingLiveProjects = existingProjects.filter(p => p.type === 'live_app');
    
    // Create a set of existing app IDs to avoid duplicates
    const existingAppIds = new Set(existingLiveProjects.map(p => p.appId).filter(Boolean));

    // Filter out projects that already exist
    const projectsToCreate = currentAppsProjects.filter(projectData =>
      !existingAppIds.has(projectData.appId)
    );
    
    if (projectsToCreate.length === 0) {
      console.log(`✅ All live projects are up to date (${existingLiveProjects.length} found)`);
      return;
    }
    
    console.log(`🆕 Creating ${projectsToCreate.length} new live projects...`);
    
    // Create only the missing live projects
    const now = Timestamp.now();
    let createdCount = 0;
    
    for (const projectData of projectsToCreate) {
      const project: Omit<Project, 'id'> = {
        ...projectData,
        createdAt: now,
        updatedAt: now
      };
      
      const projectId = await createProject(project);
      console.log(`✅ Created live project: ${project.name} (ID: ${projectId})`);
      createdCount++;
    }
    
    console.log(`🎉 Live projects sync completed!`);
    console.log(`📈 Summary: ${createdCount} created, ${existingLiveProjects.length} already existed`);
    console.log(`📱 Total live projects: ${existingLiveProjects.length + createdCount}`);
    
  } catch (error) {
    console.error('❌ Error syncing live projects:', error);
    throw error;
  }
};

/**
 * Add configuration for a new app (for developers)
 * This allows custom configuration for new apps instead of using defaults
 */
export const addAppConfiguration = (appId: string, config: {
  name: string;
  description: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  category: string;
  appPath: string;
  appColor: string;
  appIcon: string;
  tags: string[];
}) => {
  DEFAULT_APP_CONFIGS[appId] = config;
  console.log(`📝 Added custom configuration for app: ${appId}`);
};

export const getLiveProjects = async (): Promise<Project[]> => {
  try {
    const projects = await getProjects();
    return projects.filter(p => p.type === 'live_app');
  } catch (error) {
    console.error('Error getting live projects:', error);
    return [];
  }
};

export const getCustomProjects = async (): Promise<Project[]> => {
  try {
    const projects = await getProjects();
    return projects.filter(p => p.type === 'custom');
  } catch (error) {
    console.error('Error getting custom projects:', error);
    return [];
  }
};